import { useEffect, useState } from "react";
import { format, subDays } from "date-fns";
import { toast } from "sonner";
import { DownloadSimple, FilePdf, CalendarCheck, UsersThree, Storefront, MapPin, Receipt, AirplaneTilt, Path, Eye, EnvelopeSimple, PaperPlaneTilt } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { downloadReport } from "@/lib/api";

const REPORTS = [
  { key: "total-km", title: "Total KM Summary", desc: "Per-employee total kilometres, days present, avg km/day and hours for the selected range.", icon: Path, dated: true, path: "/reports/total-km", file: "total_km.csv", viewable: true },
  { key: "attendance", title: "Attendance Report", desc: "Daily login/logout, working hours, distance and stops per employee.", icon: CalendarCheck, dated: true, path: "/reports/attendance", file: "attendance.csv" },
  { key: "employees", title: "Employee Directory", desc: "Full list of employees with contact, department and status.", icon: UsersThree, dated: false, path: "/reports/employees", file: "employees.csv" },
  { key: "visits", title: "Field Visits Report", desc: "Shop visits with GPS coordinates, customer and remarks.", icon: Storefront, dated: true, path: "/reports/visits", file: "visits.csv" },
  { key: "locations", title: "Live Location Snapshot", desc: "Latest known GPS position, speed and battery of each employee.", icon: MapPin, dated: false, path: "/reports/locations", file: "live_locations.csv" },
  { key: "expenses", title: "Expenses Report", desc: "All expense claims with amounts, categories and approval status.", icon: Receipt, dated: false, path: "/reports/expenses", file: "expenses.csv" },
  { key: "leaves", title: "Leaves Report", desc: "All leave requests with dates, reasons and decisions.", icon: AirplaneTilt, dated: false, path: "/reports/leaves", file: "leaves.csv" },
];

export default function Reports() {
  const [from, setFrom] = useState(format(subDays(new Date(), 30), "yyyy-MM-dd"));
  const [to, setTo] = useState(format(new Date(), "yyyy-MM-dd"));
  const [busy, setBusy] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [digest, setDigest] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);

  useEffect(() => {
    api.get("/digest/status").then((r) => setDigest(r.data)).catch(() => {});
  }, []);

  const sendTestDigest = async () => {
    setSendingDigest(true);
    try {
      const r = await api.post("/digest/send-test");
      toast.success(`Digest for ${r.data.date} sent to ${r.data.to.join(", ")}`);
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Failed to send digest");
    } finally {
      setSendingDigest(false);
    }
  };

  const viewSummary = async () => {
    setSummaryLoading(true);
    setSummaryOpen(true);
    try {
      const r = await api.get("/reports/total-km", { params: { from_date: from, to_date: to, format: "json" } });
      setSummary(r.data);
    } catch {
      toast.error("Failed to load summary");
      setSummaryOpen(false);
    } finally {
      setSummaryLoading(false);
    }
  };

  const download = async (r, fmt) => {
    setBusy(`${r.key}-${fmt}`);
    try {
      const params = { ...(r.dated ? { from_date: from, to_date: to } : {}), format: fmt };
      const suffix = (r.dated ? `_${from}_to_${to}` : "") + `.${fmt}`;
      await downloadReport(r.path, params, r.file.replace(".csv", suffix));
      toast.success(`${r.title} downloaded (${fmt.toUpperCase()})`);
    } catch (err) {
      let msg = "Download failed.";
      try {
        if (err.response?.data instanceof Blob) {
          const text = await err.response.data.text();
          msg = `Download failed (${err.response.status}): ${text.slice(0, 120)}`;
        } else if (err.response?.status) {
          msg = `Download failed (${err.response.status})`;
        } else if (err.message) {
          msg = `Download failed: ${err.message}`;
        }
      } catch { /* keep default */ }
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="reports-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">Data Exports</p>
          <h1 className="font-heading font-black tracking-tight text-3xl">Reports</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs uppercase tracking-wider text-slate-500">Date range</span>
          <input type="date" data-testid="report-from-input" value={from} onChange={(e) => setFrom(e.target.value)} className="border border-slate-300 rounded-md px-3 py-2 text-sm font-mono-data focus:outline-none focus:ring-2 focus:ring-[#007AFF]" />
          <span className="text-slate-400 text-sm">→</span>
          <input type="date" data-testid="report-to-input" value={to} onChange={(e) => setTo(e.target.value)} className="border border-slate-300 rounded-md px-3 py-2 text-sm font-mono-data focus:outline-none focus:ring-2 focus:ring-[#007AFF]" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {REPORTS.map((r) => (
          <div key={r.key} className="border border-slate-200 rounded-md p-5 flex flex-col" data-testid={`report-card-${r.key}`}>
            <r.icon size={22} weight="duotone" className="text-[#007AFF] mb-3" />
            <h2 className="font-heading font-bold tracking-tight text-base mb-1">{r.title}</h2>
            <p className="text-sm text-slate-500 flex-1 mb-4">{r.desc}</p>
            {r.dated && <p className="text-[11px] font-mono-data text-slate-400 mb-2">{from} → {to}</p>}
            {r.viewable && (
              <button
                data-testid="view-total-km-btn"
                onClick={viewSummary}
                className="flex items-center justify-center gap-2 bg-[#007AFF] text-white rounded-md py-2 text-sm font-semibold hover:bg-[#0066d6] transition-colors duration-150 mb-2"
              >
                <Eye size={15} weight="bold" /> View Summary
              </button>
            )}
            <div className="flex gap-2">
              <button
                data-testid={`download-${r.key}-btn`}
                onClick={() => download(r, "csv")}
                disabled={busy === `${r.key}-csv`}
                className="flex-1 flex items-center justify-center gap-2 border border-slate-950 rounded-md py-2 text-sm font-semibold hover:bg-slate-950 hover:text-white transition-colors duration-150 disabled:opacity-60"
              >
                <DownloadSimple size={15} weight="bold" />
                {busy === `${r.key}-csv` ? "Preparing…" : "CSV"}
              </button>
              <button
                data-testid={`download-${r.key}-pdf-btn`}
                onClick={() => download(r, "pdf")}
                disabled={busy === `${r.key}-pdf`}
                className="flex-1 flex items-center justify-center gap-2 border border-[#FF3B30] text-[#FF3B30] rounded-md py-2 text-sm font-semibold hover:bg-[#FF3B30] hover:text-white transition-colors duration-150 disabled:opacity-60"
              >
                <FilePdf size={15} weight="bold" />
                {busy === `${r.key}-pdf` ? "Preparing…" : "PDF"}
              </button>
            </div>
          </div>
        ))}
        <div className="border border-slate-200 rounded-md p-5 flex flex-col bg-slate-50" data-testid="report-card-digest">
          <EnvelopeSimple size={22} weight="duotone" className="text-[#007AFF] mb-3" />
          <h2 className="font-heading font-bold tracking-tight text-base mb-1">Daily Email Digest</h2>
          <p className="text-sm text-slate-500 flex-1 mb-2">
            Previous day's attendance summary emailed automatically every morning at {digest?.send_hour_ist ?? 8}:00 AM IST.
          </p>
          <p className="text-[11px] font-mono-data text-slate-500 mb-1">To: {digest?.recipients?.join(", ") || "—"}</p>
          {digest?.sender_domain_status && digest.sender_domain_status !== "verified" && digest.sender_domain_status !== "sandbox" && (
            <p className="text-[11px] text-amber-600 mb-1" data-testid="digest-domain-warning">
              Sender domain {digest.sender_email?.split("@")[1]} is not verified in Resend ({digest.sender_domain_status.replace("_", " ")}) — emails fall back to the sandbox sender and deliver to {digest.recipients?.[0]} only. Add the DNS records at resend.com/domains to enable all recipients.
            </p>
          )}
          {digest?.sender_domain_status === "verified" && (
            <p className="text-[11px] text-emerald-600 mb-1" data-testid="digest-domain-ok">Sender domain verified — delivering to all recipients.</p>
          )}
          <p className={`text-[11px] font-semibold mb-3 ${digest?.configured ? "text-[#007AFF]" : "text-amber-600"}`} data-testid="digest-status">
            {digest ? (digest.configured ? `Active${digest.last_sent_date ? ` · last sent ${digest.last_sent_date}` : ""}` : "Waiting for Resend API key") : "…"}
          </p>
          <button
            data-testid="send-test-digest-btn"
            onClick={sendTestDigest}
            disabled={sendingDigest}
            className="flex items-center justify-center gap-2 border border-slate-950 rounded-md py-2 text-sm font-semibold hover:bg-slate-950 hover:text-white transition-colors duration-150 disabled:opacity-60"
          >
            <PaperPlaneTilt size={15} weight="bold" />
            {sendingDigest ? "Sending…" : "Send Test Digest"}
          </button>
        </div>
      </div>

      <Dialog open={summaryOpen} onOpenChange={setSummaryOpen}>
        <DialogContent className="max-w-3xl" data-testid="total-km-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Total KM Summary</DialogTitle>
            <DialogDescription className="font-mono-data text-xs">{from} → {to}</DialogDescription>
          </DialogHeader>
          {summaryLoading ? (
            <p className="text-sm text-slate-400 py-6" data-testid="total-km-loading">Crunching the numbers…</p>
          ) : (
            <div className="border border-slate-200 rounded-md overflow-hidden max-h-[60vh] overflow-y-auto">
              <table className="w-full text-sm" data-testid="total-km-table">
                <thead className="sticky top-0 bg-slate-50">
                  <tr className="border-b border-slate-200 text-left">
                    {["Employee", "Department", "Days Present", "Total KM", "Avg KM/Day", "Hours"].map((h) => (
                      <th key={h} className="px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(summary?.rows || []).map((e) => (
                    <tr key={e.employee_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`total-km-row-${e.employee_id}`}>
                      <td className="px-4 py-2.5">
                        <p className="font-medium">{e.name}</p>
                        <p className="text-xs text-slate-500 font-mono-data">{e.employee_id}</p>
                      </td>
                      <td className="px-4 py-2.5 text-slate-600">{e.department || "—"}</td>
                      <td className="px-4 py-2.5 font-mono-data">{e.days_present}</td>
                      <td className="px-4 py-2.5 font-mono-data font-semibold">{e.total_km} km</td>
                      <td className="px-4 py-2.5 font-mono-data">{e.avg_km_per_day} km</td>
                      <td className="px-4 py-2.5 font-mono-data">{e.total_hours}</td>
                    </tr>
                  ))}
                  {summary && summary.rows.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-400">No employees found in this range.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
